import React from 'react';
import { Box } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import WorkIcon from '@mui/icons-material/Work';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import HistoryIcon from '@mui/icons-material/History';
import BugReportIcon from '@mui/icons-material/BugReport';
import InsightsIcon from '@mui/icons-material/Insights';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

export const MANAGEMENT_VIEW_KEYS = ['actions_hub', 'team', 'clients', 'business_development', 'analytics', 'fee_proposals', 'activity'];

function buildClientsIcon() {
    return (
        <Box sx={{ position: 'relative', width: 20, height: 20, display: 'inline-block' }}>
            <PersonIcon sx={{ fontSize: 20 }} />
            <BusinessCenterIcon sx={{ position: 'absolute', right: -3, bottom: -3, fontSize: 12 }} />
        </Box>
    );
}

function withView(items) {
    return items.map((item) => ({ ...item, view: item.key }));
}


export function getDashboardNavItems({ role = '', portalMode = '', tenantCapabilities = [], featureVisibility = {} } = {}) {
    const normalizedRole = String(role || '').trim().toLowerCase();
    const normalizedMode = String(portalMode || '').trim().toLowerCase();
    const caps = Array.isArray(tenantCapabilities)
        ? tenantCapabilities.map((cap) => String(cap || '').trim().toLowerCase())
        : [];
    const isDeveloper = normalizedRole === 'developer' || normalizedMode === 'developer';
    const isFunder = normalizedRole === 'funder' || normalizedMode === 'funder';
    const isAdmin = normalizedRole === 'admin' && normalizedMode !== 'developer';
    const isFinance = normalizedRole === 'finance' && normalizedMode !== 'developer';
    const isSurveyor = (normalizedRole === 'surveyor' || normalizedRole === 'standard') && normalizedMode !== 'developer';
    const canViewFeasibility = Boolean(
        featureVisibility?.clientdeal_portal
        ?? caps.includes('feasibility_reports')
    );

    if (isDeveloper) {
        return withView([
            { key: 'overview', label: 'Overview', icon: <DashboardIcon fontSize="small" /> },
            { key: 'reports', label: 'Projects', icon: <FolderIcon fontSize="small" /> },
            { key: 'analytics', label: 'Analytics', icon: <BarChartIcon fontSize="small" /> },
            { key: 'activity', label: 'Activity', icon: <HistoryIcon fontSize="small" /> },
            { key: 'request_valuation', label: 'Request Valuation', icon: <RequestPageIcon fontSize="small" /> },
            { key: 'settings', label: 'Settings', icon: <SettingsIcon fontSize="small" /> },
        ]);
    }

    if (isFunder) {
        return withView([
            { key: 'overview', label: 'Overview', icon: <DashboardIcon fontSize="small" /> },
            { key: 'reports', label: 'Projects', icon: <FolderIcon fontSize="small" /> },
            { key: 'analytics', label: 'Analytics', icon: <BarChartIcon fontSize="small" /> },
            { key: 'activity', label: 'Activity', icon: <HistoryIcon fontSize="small" /> },
            { key: 'settings', label: 'Settings', icon: <SettingsIcon fontSize="small" /> },
        ]);
    }

    const items = [
        { key: 'overview', label: 'Overview', icon: <DashboardIcon fontSize="small" /> },
        { key: 'reports', label: 'Projects', icon: <FolderIcon fontSize="small" /> },
        { key: 'calendar', label: 'Calendar', icon: <CalendarMonthIcon fontSize="small" /> },
        { key: 'activity', label: 'Activity', icon: <HistoryIcon fontSize="small" /> },
    ];

    if (isAdmin) {
        const activityIndex = items.findIndex((item) => item.key === 'activity');
        if (activityIndex !== -1) items.splice(activityIndex, 1);

        items.push(
            { key: 'projects', label: 'Instructions', icon: <WorkIcon fontSize="small" /> },
            { key: 'financials', label: 'Financials', icon: <AttachMoneyIcon fontSize="small" /> },
            { key: 'actions_hub', label: 'Actions Hub', icon: <FactCheckOutlinedIcon fontSize="small" /> },
            { key: 'analytics', label: 'Analytics', icon: <BarChartIcon fontSize="small" /> },
            { key: 'fee_proposals', label: 'Fee Proposals', icon: <RequestPageIcon fontSize="small" /> },
            { key: 'activity', label: 'Activity', icon: <HistoryIcon fontSize="small" /> },
            { key: 'team', label: 'Team', icon: <GroupIcon fontSize="small" /> },
            { key: 'clients', label: 'Clients', icon: buildClientsIcon() },
            { key: 'business_development', label: 'Business Development', icon: <TravelExploreIcon fontSize="small" /> },
        );
    } else if (isFinance) {
        const activityIndex = items.findIndex((item) => item.key === 'activity');
        if (activityIndex !== -1) items.splice(activityIndex, 1);

        items.push(
            { key: 'financials', label: 'Financials', icon: <AttachMoneyIcon fontSize="small" /> },
            { key: 'activity', label: 'Activity', icon: <HistoryIcon fontSize="small" /> },
        );
    } else if (isSurveyor) {
        items.push(
            { key: 'actions_hub', label: 'Actions Hub', icon: <FactCheckOutlinedIcon fontSize="small" /> },
            { key: 'analytics', label: 'Analytics', icon: <BarChartIcon fontSize="small" /> },
            { key: 'fee_proposals', label: 'Fee Proposals', icon: <RequestPageIcon fontSize="small" /> },
            { key: 'team', label: 'Team', icon: <GroupIcon fontSize="small" /> },
            { key: 'clients', label: 'Clients', icon: buildClientsIcon() },
            { key: 'business_development', label: 'Business Development', icon: <TravelExploreIcon fontSize="small" /> },
        );
    }

    if (canViewFeasibility) {
        items.push({
            key: 'clientdeal',
            label: 'Feasibility',
            icon: <InsightsIcon fontSize="small" />
        });
    }

    items.push({ key: 'settings', label: 'Settings', icon: <SettingsIcon fontSize="small" /> });
    if (isAdmin) items.push({ key: 'issue_reports', label: 'Issue Reports', icon: <BugReportIcon fontSize="small" /> });
    return withView(items);
}